// useExplainBlock — React-hook som anropar ai-explain-block edge-function
// och returnerar natural-language-förklaring av ett block + dess
// connector-kontext. Lazy: fetch körs inte förrän caller anropar
// `fetch()`-funktionen (t.ex. när user öppnar popover).
//
// Side-effects: ingen — hooken är ren React-state + fetch. Edge-fn själv
// är admin-agnostisk (öppet för alla tiers med credits) eftersom
// explain är en READ-operation på workspace-data user redan ser.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type {
  WorkflowBlock,
  Connector,
  WorkflowState,
} from '@/types/workflow';

export type ExplainMode = 'brief' | 'detailed';

export interface UseExplainBlockResult {
  /** Senaste förklaring från AI:n. Null innan fetch körts. */
  explanation: string | null;
  isLoading: boolean;
  error: string | null;
  /**
   * Q-045: edge-fn:ns correlation-id för senaste request (från `X-Correlation-ID`-
   * response-header eller body-fält). Null innan första fetch:en. Visas i
   * error-toast så user kan ge support en sökbar referens.
   */
  correlationId: string | null;
  /** Kör edge-RPC:n. Idempotent — samma `block.id` + `lang` + `mode` ger samma cached resultat. */
  fetch: (lang?: 'sv' | 'en', mode?: ExplainMode) => Promise<void>;
  /** Rensa state till initial (innan nästa block:s popover öppnas). */
  reset: () => void;
}

export interface UseExplainBlockInput {
  /** Block att förklara. */
  block: WorkflowBlock | null;
  /** Sheet:s connectors — används för att hitta in/ut-grannar. */
  connectors: Connector[];
  /** Sheet:s blocks — för att kunna namnge grannarna i prompten. */
  sheetBlocks: WorkflowBlock[];
  /** Workflow-namn för context. */
  workflowName?: string;
}

function buildContext(input: UseExplainBlockInput) {
  const { block, connectors, sheetBlocks } = input;
  if (!block) return null;
  const incoming = connectors
    .filter((c) => c.toId === block.id)
    .map((c) => ({
      fromId: c.fromId,
      fromName: sheetBlocks.find((b) => b.id === c.fromId)?.config.name ?? c.fromId,
      label: c.label,
    }));
  const outgoing = connectors
    .filter((c) => c.fromId === block.id)
    .map((c) => ({
      toId: c.toId,
      toName: sheetBlocks.find((b) => b.id === c.toId)?.config.name ?? c.toId,
      label: c.label,
    }));
  return {
    block: {
      id: block.id,
      type: block.type,
      name: block.config.name,
      description: block.config.description,
      icon: block.config.icon,
    },
    incoming,
    outgoing,
    workflowName: input.workflowName,
  };
}

export function useExplainBlock(input: UseExplainBlockInput): UseExplainBlockResult {
  const [explanation, setExplanation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);

  // S-029: per-call AbortController så att en pågående fetch avbryts om user
  // klickar på ett annat block (eller stänger panel:en) innan svaret kommit.
  // Förhindrar duplicate-credit-debit i framtida credit-gating + race där
  // ett gammalt svar skriver över ett nytt blocks förklaring.
  const fetchAbortRef = useRef<AbortController | null>(null);

  // Cleanup vid unmount + vid block-byte: avbryt eventuell pågående fetch.
  useEffect(() => {
    return () => {
      fetchAbortRef.current?.abort();
      fetchAbortRef.current = null;
    };
  }, [input.block?.id]);

  const fetchFn = useCallback(
    async (lang: 'sv' | 'en' = 'sv', mode: ExplainMode = 'brief') => {
      if (!input.block) {
        setError('no-block-selected');
        return;
      }
      // Avbryt en eventuellt pågående fetch innan vi startar en ny.
      fetchAbortRef.current?.abort();
      const controller = new AbortController();
      fetchAbortRef.current = controller;
      setIsLoading(true);
      setError(null);
      setCorrelationId(null);
      try {
        const context = buildContext(input);
        if (!context) {
          setError('no-context');
          return;
        }
        const { data: session } = await supabase.auth.getSession();
        const token = session.session?.access_token;
        // S-109: ai-explain-block-edge-fn:n har sedan S-015 hård JWT-auth-
        // gate (`verifyUserJWT` kräver `claims.sub`). Anon-keyens JWT har
        // `role: 'anon'` och saknar `sub` → 401 `invalid-token`. Avbryt
        // tidigt med explicit "not-authenticated"-state istället för att
        // skicka en garanterad-misslyckad request. Matchar Q-044/S-107-
        // mönstret för admin-AI.
        if (!token) {
          setError('not-authenticated');
          return;
        }
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-explain-block`;
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };
        const resp = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ ...context, lang, mode }),
          signal: controller.signal,
        });
        // Q-045: läs correlation-id från response-headern först (alltid satt
        // av edge-fn:n via `withCorrelationHeaders`), så support kan refereras
        // även när body-parsen failar (t.ex. 502 från upstream).
        const respCorrelationId = resp.headers.get('X-Correlation-ID');
        if (respCorrelationId) setCorrelationId(respCorrelationId);
        if (!resp.ok) {
          if (resp.status === 404) {
            setError('edge-function-not-deployed');
          } else {
            const body = await resp.text().catch(() => '');
            setError(`edge-${resp.status}: ${body.slice(0, 120)}`);
          }
          return;
        }
        const data = (await resp.json().catch(() => null)) as
          | { explanation?: string; error?: string; correlationId?: string }
          | null;
        // Q-045: body-id som fallback om response-headern råkade strippas
        // av en proxy. Body har samma värde som headern (edge-fn:n
        // returnerar båda).
        if (data?.correlationId) setCorrelationId(data.correlationId);
        if (!data || data.error) {
          setError(data?.error ?? 'empty-response');
          return;
        }
        setExplanation(data.explanation ?? '');
      } catch (e) {
        // AbortError → tyst returnering. Den nya fetch:en (eller unmount)
        // har redan rensat state.
        if ((e as Error).name === 'AbortError') return;
        setError((e as Error).message ?? 'network-error');
      } finally {
        // Bara den aktiva controllern äger loading-state — annars kan en
        // avbruten gammal fetch's finally:y nollställa loading medan en
        // nyare fetch fortfarande pågår.
        if (fetchAbortRef.current === controller) {
          setIsLoading(false);
        }
      }
    },
    [input],
  );

  const reset = useCallback(() => {
    fetchAbortRef.current?.abort();
    fetchAbortRef.current = null;
    setExplanation(null);
    setError(null);
    setCorrelationId(null);
    setIsLoading(false);
  }, []);

  return { explanation, isLoading, error, correlationId, fetch: fetchFn, reset };
}

/** Export buildContext för tester + för att produkter som vill bygga egna UIs
 * kan återanvända context-shape:en. */
export { buildContext };
export type { WorkflowState };
